import moment from 'moment';
import markProcessedTemplate from '../expense-review-details/modals/mark-processed/mark-processed.html';
import MarkProcessedModalController from '../expense-review-details/modals/mark-processed/mark-processed.controller';

class ExpenseReviewController {
  constructor(toastr, $scope, $element, $http, $rootScope, $state, $uibModal, TravelExpenseService) {
    'ngInject';

    this.$http = $http;
    this.state = $state;
    this.$scope = $scope;
    this.$rootScope = $rootScope;
    this.toastr = toastr;
    this.modal = $uibModal;
    this.travelExpenseService = TravelExpenseService;

    this.loading = true;
    this.actionBusyId = null;
    this.expenseReports = [];
    this.currentReports = [];
    this.inProgressImports = [];
    this.closedReports = [];
    this.metrics = {
      openReports: 0,
      awaitingApproval: 0,
      reimbursementReview: 0,
      readyToProcess: 0,
      pendingReimbursement: 0,
      ytdProcessed: 0,
      inProgressImports: 0
    };

    this.$http.get(this.$rootScope.apiHost + '/api/admin/expense-reports', { withCredentials: true }).then((success) => {
      this.expenseReports = success.data || [];
      this.refreshListsAndMetrics();
      this.loading = false;
    }, () => {
      this.loading = false;
      this.toastr.error('Could not load expense reports.', 'Error');
    });
  }

  refreshListsAndMetrics() {
    const reports = this.expenseReports || [];
    this.currentReports = reports.filter((report) => !this.isProcessed(report.status) && !this.isProxyReport(report));
    this.inProgressImports = reports.filter((report) => this.isProxyReport(report) && !this.isProcessed(report.status));
    this.closedReports = reports.filter((report) => this.isProcessed(report.status));
    this.metrics = this.computeMetrics(reports);
  }

  computeMetrics(reports) {
    const metrics = {
      openReports: 0,
      awaitingApproval: 0,
      reimbursementReview: 0,
      readyToProcess: 0,
      pendingReimbursement: 0,
      ytdProcessed: 0,
      inProgressImports: 0
    };

    const currentYear = new Date().getFullYear();

    (reports || []).forEach((report) => {
      const status = (report.status || '').toUpperCase();

      if (this.isProxyReport(report) && !this.isProcessed(status)) {
        metrics.inProgressImports += 1;
      }

      if (!this.isProcessed(status) && !this.isProxyReport(report)) {
        metrics.openReports += 1;
      }

      if (status === 'SUBMITTED' && !this.isProxyReport(report)) {
        metrics.awaitingApproval += 1;
      }

      if (status === 'REIMBURSEMENT_REQUESTED' && !this.isProxyReport(report)) {
        metrics.reimbursementReview += 1;
      }

      if (status === 'REIMBURSEMENT_APPROVED' && !this.isProxyReport(report)) {
        metrics.readyToProcess += 1;
      }

      if (!this.isProxyReport(report)
          && (status === 'REIMBURSEMENT_REQUESTED' || status === 'REIMBURSEMENT_APPROVED')) {
        metrics.pendingReimbursement += this.getActualAmount(report) || 0;
      }

      if (this.isProcessed(status) && this.isProcessedInYear(report, currentYear)) {
        metrics.ytdProcessed += this.getActualAmount(report) || 0;
      }
    });

    metrics.pendingReimbursement = Math.round(metrics.pendingReimbursement * 100) / 100;
    metrics.ytdProcessed = Math.round(metrics.ytdProcessed * 100) / 100;

    return metrics;
  }

  isProcessed(status) {
    return (status || '').toUpperCase() === 'PROCESSED';
  }

  isProxyReport(report) {
    return !!(report && report.proxyCreatedByUserId);
  }

  isProcessedInYear(report, year) {
    if (!report || !report.processedDate) {
      return true;
    }

    const processed = new Date(report.processedDate);
    if (isNaN(processed.getTime())) {
      return true;
    }

    return processed.getFullYear() === year;
  }

  getStatus(status) {
    const statusDetails = this.travelExpenseService.getTravelExpenseStatusDetails(status);
    return statusDetails ? statusDetails.label : '';
  }

  isRejectedStatus(status) {
    const normalized = (status || '').toUpperCase();
    return normalized === 'REJECTED'
      || normalized === 'REIMBURSEMENT_REJECTED'
      || normalized === 'REIMBURSMENT_REJECTED';
  }

  isGeneralBusiness(expenseReport) {
    return !!expenseReport && expenseReport.reportType === 'GENERAL_BUSINESS';
  }

  isOtherDirectCosts(expenseReport) {
    return !!expenseReport && expenseReport.reportType === 'OTHER_DIRECT_COSTS';
  }

  getReportTypeLabel(expenseReport) {
    if (this.isGeneralBusiness(expenseReport)) {
      return 'General Business';
    }
    if (this.isOtherDirectCosts(expenseReport)) {
      return 'Other Direct Costs';
    }
    return 'Travel';
  }

  formatDates(expenseReport) {
    if (expenseReport && expenseReport.expenses && expenseReport.expenses.length) {
      return this.travelExpenseService.getDatesOfTravel(expenseReport) || '';
    }
    return '';
  }

  getEstimatedAmount(expenseReport) {
    return this.travelExpenseService.getTotalForExpenseReport(expenseReport) || 0;
  }

  getActualAmount(expenseReport) {
    return this.travelExpenseService.getTotalForExpenseReport(expenseReport, true) || 0;
  }

  openReport(expenseReport) {
    let state = 'expense-review-details';
    if (this.isGeneralBusiness(expenseReport)) {
      state = 'general-business-review-details';
    } else if (this.isOtherDirectCosts(expenseReport)) {
      state = 'other-direct-costs-review-details';
    }
    this.state.go(state, { expenseReportId: expenseReport.id });
  }

  editProxyImport(expenseReport) {
    if (!expenseReport || !expenseReport.id) {
      return;
    }

    const params = { expenseReportId: expenseReport.id, proxyImport: 'true' };
    const statusDetails = this.travelExpenseService.getTravelExpenseStatusDetails(expenseReport.status);
    const postTravel = statusDetails && statusDetails.phase === 'Post-Travel';

    if (this.isGeneralBusiness(expenseReport)) {
      this.state.go(postTravel ? 'general-business-actuals' : 'general-business', params);
      return;
    }
    if (this.isOtherDirectCosts(expenseReport)) {
      this.state.go(postTravel ? 'other-direct-costs-actuals' : 'other-direct-costs', params);
      return;
    }
    this.state.go(postTravel ? 'travel-expense-actuals' : 'travel-expense', params);
  }

  isActionBusy(expenseReport) {
    return this.actionBusyId != null && expenseReport && this.actionBusyId === expenseReport.id;
  }

  finalizeProxyImport(expenseReport) {
    if (!expenseReport || this.actionBusyId) {
      return;
    }

    const existingProcessedDate = this.toDateString(expenseReport.processedDate);
    const finalize = (processedDate, notify) => {
      if (!processedDate) {
        return;
      }
      this.actionBusyId = expenseReport.id;
      this.travelExpenseService.finalizeProxyImport(expenseReport.id, processedDate, notify).then((response) => {
        this.replaceReport(response.data || Object.assign({}, expenseReport, {
          status: 'PROCESSED',
          processedDate
        }));
        this.toastr.success('Proxy import finalized.', 'Processed');
      }, (err) => {
        const message = (err && err.data && (err.data.message || err.data.error))
          || 'Could not finalize the proxy import.';
        this.toastr.error(message, 'Error');
      }).finally(() => {
        this.actionBusyId = null;
      });
    };

    if (existingProcessedDate) {
      if (!window.confirm('Finalize this proxy import and move it to Closed Reports?')) {
        return;
      }
      finalize(existingProcessedDate, false);
      return;
    }

    this.showMarkProcessedDialog(expenseReport).then((result) => {
      if (!result || !result.processedDate) {
        return;
      }
      finalize(result.processedDate, result.notify);
    });
  }

  resumeProxyImport(expenseReport) {
    if (!expenseReport || this.actionBusyId) {
      return;
    }
    if (!window.confirm('Resume editing this proxy import? It will move back to In Progress Imports.')) {
      return;
    }

    this.actionBusyId = expenseReport.id;
    this.travelExpenseService.resumeProxyImport(expenseReport.id).then((response) => {
      this.replaceReport(response.data);
      this.toastr.success('Proxy import reopened for editing.', 'Resumed');
    }, (err) => {
      const message = (err && err.data && (err.data.message || err.data.error))
        || 'Could not resume the proxy import.';
      this.toastr.error(message, 'Error');
    }).finally(() => {
      this.actionBusyId = null;
    });
  }

  canDeleteReport(expenseReport) {
    return !!(expenseReport && expenseReport.id && !expenseReport.invoiced);
  }

  deleteExpenseReport(expenseReport) {
    if (!this.canDeleteReport(expenseReport) || this.actionBusyId) {
      if (expenseReport && expenseReport.invoiced) {
        this.toastr.warning('This expense report is on an invoice and cannot be deleted.', 'Cannot Delete');
      }
      return;
    }

    const name = expenseReport.name || 'this expense report';
    if (!window.confirm('Delete "' + name + '"? This cannot be undone.')) {
      return;
    }

    this.actionBusyId = expenseReport.id;
    this.travelExpenseService.deleteExpenseReport(expenseReport.id).then(() => {
      this.expenseReports = (this.expenseReports || []).filter((report) => report.id !== expenseReport.id);
      this.refreshListsAndMetrics();
      this.toastr.success('Expense report deleted.', 'Deleted');
    }, (err) => {
      const message = (err && err.data && (err.data.message || err.data.error))
        || 'Could not delete expense report.';
      this.toastr.error(message, 'Error');
    }).finally(() => {
      this.actionBusyId = null;
    });
  }

  replaceReport(updated) {
    if (!updated || updated.id == null) {
      return;
    }
    const index = (this.expenseReports || []).findIndex((report) => report.id === updated.id);
    if (index >= 0) {
      this.expenseReports[index] = Object.assign({}, this.expenseReports[index], updated);
    } else {
      this.expenseReports.push(updated);
    }
    this.refreshListsAndMetrics();
  }

  showMarkProcessedDialog(expenseReport) {
    const reimbursementAmount = this.travelExpenseService.formatNumber(
      this.travelExpenseService.getEmployeeReimbursementTotal(expenseReport)
    );
    const totalCost = this.travelExpenseService.formatNumber(this.getActualAmount(expenseReport));

    return this.modal.open({
      template: markProcessedTemplate,
      controller: MarkProcessedModalController,
      controllerAs: '$ctrl',
      windowClass: 'hmodal-success',
      resolve: {
        modalContext: () => ({
          reportName: expenseReport.name,
          reimbursementAmount,
          totalCost
        })
      }
    }).result.then((result) => result, () => undefined);
  }

  toDateString(value) {
    if (!value) {
      return null;
    }
    if (typeof value === 'string') {
      return value.substring(0, 10);
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  }
}

export default ExpenseReviewController;



// WEBPACK FOOTER //
// ./client/pages/admin/expense-review/expense-review.controller.js